/**
 * TodayCalendarView.jsx
 *
 * Clean single-day view of just today's events from all sources
 * (Outlook/Calendar.app, Nylas, local). Sorted by start time, all-day
 * events floated to the top.
 */
import React, { useMemo } from 'react';
import { Calendar, Clock, MapPin, Video, Plus } from 'lucide-react';

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const fmtTime12 = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const TodayCalendarView = ({
  outlookEvents = [],
  nylasEvents = [],
  localEvents = [],
  onEventClick,
}) => {
  const today = useMemo(() => new Date(), []);
  const todayLabel = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const eventsToday = useMemo(() => {
    const combined = [];

    // Outlook / Calendar.app — use ISO start; respect all-day flag
    outlookEvents.forEach((ev) => {
      const iso = ev.start_time || ev.start;
      if (!iso) return;
      const d = new Date(iso);
      if (isNaN(d.getTime()) || !isSameDay(d, today)) return;
      const endIso = ev.end_time || ev.end;
      const endDate = endIso ? new Date(endIso) : null;
      combined.push({
        id: ev.id || ev.graph_id,
        title: ev.title || ev.subject || ev.name || 'Event',
        all_day: !!(ev.all_day || ev.is_all_day),
        start: d,
        end: endDate && !isNaN(endDate.getTime()) ? endDate : null,
        location: ev.location || '',
        online_meeting_url: ev.online_meeting_url || null,
        source: ev.source || 'outlook',
        category: ev.category || ev.type || 'other',
        raw: ev,
      });
    });

    // Nylas events
    nylasEvents.forEach((ev) => {
      const startTs = ev.when?.start_time;
      const endTs = ev.when?.end_time;
      let start = null;
      let end = null;
      if (startTs) start = new Date(startTs * 1000);
      if (endTs) end = new Date(endTs * 1000);
      if (!start || isNaN(start.getTime()) || !isSameDay(start, today)) return;
      combined.push({
        id: ev.id || ev.nylas_id,
        title: ev.title || 'Event',
        all_day: !!ev.when?.start_date,
        start,
        end,
        location: ev.location || '',
        online_meeting_url: null,
        source: 'nylas',
        category: ev.category || 'meeting',
        raw: ev,
      });
    });

    // Local events — typically have day/month/year + time string "9:00 AM"
    localEvents.forEach((ev) => {
      const d = ev.start_time
        ? new Date(ev.start_time)
        : (ev.day && (ev.month !== undefined) && ev.year)
          ? new Date(ev.year, ev.month, ev.day)
          : null;
      if (!d || isNaN(d.getTime()) || !isSameDay(d, today)) return;
      combined.push({
        id: ev.id,
        title: ev.title || ev.name || 'Event',
        all_day: !!ev.all_day,
        start: d,
        end: ev.end_time ? new Date(ev.end_time) : null,
        location: ev.location || '',
        online_meeting_url: ev.online_meeting_url || null,
        source: 'local',
        category: ev.category || ev.type || 'other',
        raw: ev,
      });
    });

    // Sort: all-day first, then chronological by start
    combined.sort((a, b) => {
      if (a.all_day && !b.all_day) return -1;
      if (!a.all_day && b.all_day) return 1;
      return a.start.getTime() - b.start.getTime();
    });
    return combined;
  }, [outlookEvents, nylasEvents, localEvents, today]);

  return (
    <div className="bg-black border border-gray-400 rounded-xl p-4 md:p-6 mb-3 md:mb-6 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 flex-1 overflow-auto" data-testid="today-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-2xl font-bold text-white flex items-center gap-2">
            <Calendar size={20} className="text-sky-400" />
            Today
          </h2>
          <p className="text-xs md:text-sm text-gray-400 mt-1">{todayLabel}</p>
        </div>
        <span className="text-xs text-gray-500" data-testid="today-event-count">
          {eventsToday.length} event{eventsToday.length === 1 ? '' : 's'}
        </span>
      </div>

      {eventsToday.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Calendar size={48} className="mx-auto mb-3 text-gray-700" />
          <p className="text-sm">Nothing scheduled for today.</p>
          <p className="text-xs text-gray-600 mt-1">A clear day — make the most of it.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {eventsToday.map((ev) => (
            <li
              key={ev.id || `${ev.title}-${ev.start.getTime()}`}
              onClick={() => onEventClick && onEventClick(ev.raw || ev)}
              className="group flex items-start gap-3 bg-gray-900/40 hover:bg-gray-800/60 border border-gray-800 hover:border-sky-500/40 rounded-lg p-3 cursor-pointer transition-all"
              data-testid={`today-event-${ev.id}`}
            >
              {/* Time column */}
              <div className="flex-shrink-0 w-24 text-right">
                {ev.all_day ? (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/15 text-amber-300 rounded text-[10px] font-semibold uppercase tracking-wide border border-amber-500/30">
                    All Day
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-bold text-white">{fmtTime12(ev.start)}</div>
                    {ev.end && <div className="text-[10px] text-gray-500">→ {fmtTime12(ev.end)}</div>}
                  </>
                )}
              </div>

              {/* Body */}
              <div className="flex-1 min-w-0">
                <div className="text-sm md:text-base font-medium text-white truncate">{ev.title}</div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {ev.location && (
                    <span className="text-[11px] text-gray-400 flex items-center gap-1 truncate max-w-xs">
                      <MapPin size={11} /> {ev.location}
                    </span>
                  )}
                  {ev.online_meeting_url && (
                    <a
                      href={ev.online_meeting_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                      data-testid={`today-event-join-${ev.id}`}
                    >
                      <Video size={11} /> Join
                    </a>
                  )}
                  {ev.category && ev.category !== 'other' && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-500">{ev.category}</span>
                  )}
                </div>
              </div>

              {/* Now indicator */}
              {!ev.all_day && ev.start <= new Date() && (!ev.end || ev.end >= new Date()) && (
                <span className="flex-shrink-0 self-center px-2 py-0.5 bg-emerald-500/15 text-emerald-300 rounded text-[10px] font-semibold uppercase border border-emerald-500/40 flex items-center gap-1">
                  <Clock size={10} /> Now
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TodayCalendarView;
